import * as React from "react";

type Props = { className?: string; density?: number };

/**
 * Ambient background: slow-drifting gold particles + 3 large
 * blurred orbs that gently breathe. Designed to sit behind app
 * surfaces without competing for attention.
 */
export function AmbientCanvas({ className, density = 60 }: Props) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let width = 0, height = 0;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      width = r.width; height = r.height;
      canvas.width  = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    type P = { x: number; y: number; vx: number; vy: number; r: number; a: number };
    const particles: P[] = Array.from({ length: density }).map(() => mk());
    function mk(): P {
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.15,
        vy: -0.05 - Math.random() * 0.15,
        r: 0.6 + Math.random() * 1.6,
        a: 0.15 + Math.random() * 0.4
      };
    }

    type Orb = { cx: number; cy: number; r: number; hue: string; phase: number; speed: number };
    const orbs: Orb[] = [
      { cx: 0.20, cy: 0.30, r: 280, hue: "rgba(201,169,106,0.18)", phase: 0,       speed: 0.0006 },
      { cx: 0.80, cy: 0.20, r: 240, hue: "rgba(224,195,136,0.12)", phase: 1.3,     speed: 0.0008 },
      { cx: 0.50, cy: 0.80, r: 320, hue: "rgba(140,115,65,0.16)",  phase: 2.6,     speed: 0.0005 }
    ];

    let raf = 0;
    let t = 0;
    const draw = () => {
      t += reduced ? 0 : 1;
      ctx.clearRect(0, 0, width, height);

      // Orbs.
      for (const o of orbs) {
        const bob = Math.sin(t * o.speed + o.phase) * 18;
        const grad = ctx.createRadialGradient(
          o.cx * width + bob, o.cy * height + bob, 0,
          o.cx * width + bob, o.cy * height + bob, o.r
        );
        grad.addColorStop(0, o.hue);
        grad.addColorStop(1, "rgba(10,10,11,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      }

      // Particles — gold sparkles drifting upward.
      if (!reduced) {
        for (const p of particles) {
          p.x += p.vx; p.y += p.vy;
          if (p.y < -10 || p.x < -10 || p.x > width + 10) Object.assign(p, mk(), { y: height + 10 });
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(224,195,136,${p.a.toFixed(3)})`;
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [density, reduced]);

  return <canvas ref={canvasRef} className={className} aria-hidden />;
}

function useReducedMotion() {
  const [v, setV] = React.useState(false);
  React.useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setV(m.matches);
    const h = () => setV(m.matches);
    m.addEventListener("change", h);
    return () => m.removeEventListener("change", h);
  }, []);
  return v;
}
